// Session helpers shared by App.js, the fetch interceptor (index.js) and
// authFetch (utils/api.js). Pure where possible so they are unit-testable.
//
// Three concerns live here:
//
//   1. returnTo — every "you need to log in" redirect carries the page the
//      user was heading to (`/login?returnTo=/live?bot=..&session=..`) and
//      handleLogin honours it, so a notification deep link survives a login
//      instead of dumping the user on My Bots. `sanitizeReturnTo` is the one
//      gate: same-origin absolute paths only (no `//evil`, no scheme, never
//      an auth page).
//
//   2. Sliding session — the backend JWT carries `iat`/`exp` (see backend
//      controller/authentication.py). Once a token is past HALF its life the
//      portal silently re-mints it via POST /api/auth/refresh, so anyone who
//      opens the portal at least once per lifetime stays signed in and only
//      real inactivity expires. `shouldRefreshToken` decides, `refreshSession`
//      performs the exchange.
//
//   3. Telegram login — the notification button is a Telegram `login_url`
//      button: Telegram appends the tapping user's identity + `hash` to the
//      deep link. `extractTelegramLogin` splits those fields from our own
//      (`bot`, `session`) and `exchangeTelegramLogin` swaps them for a session
//      via POST /api/auth/telegram/login (verified server-side).

const API = () => process.env.REACT_APP_API_BASE_URL;

// Auth pages must never be a returnTo target (would loop / lose the intent).
const AUTH_PATHS = ["/login", "/register", "/forgot-password", "/reset-password"];

// ---------------------------------------------------------------------------
// returnTo
// ---------------------------------------------------------------------------

/** Accept only same-origin absolute paths. Returns null when unusable. */
export function sanitizeReturnTo(raw) {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value.startsWith("/")) return null;
  if (value.startsWith("//")) return null; // protocol-relative -> open redirect
  if (/[\\\r\n]/.test(value)) return null; // header/backslash tricks
  if (/^\/[^/?#]*:/.test(value)) return null; // "/javascript:..." style oddities
  const pathOnly = value.split(/[?#]/)[0];
  if (AUTH_PATHS.some((p) => pathOnly === p || pathOnly.startsWith(p + "/"))) return null;
  if (pathOnly === "/") return null; // the default landing already handles "/"
  return value;
}

/** `pathname + search (+ hash)` of a location-like object, or null if it is
 *  not worth returning to (auth pages, root). */
export function currentReturnTo(loc = window.location) {
  if (!loc) return null;
  const path = `${loc.pathname || ""}${loc.search || ""}${loc.hash || ""}`;
  return sanitizeReturnTo(path);
}

/** `/login` or `/login?returnTo=<encoded path>` for the given location. */
export function loginPathFor(loc = window.location) {
  const rt = currentReturnTo(loc);
  return rt ? `/login?returnTo=${encodeURIComponent(rt)}` : "/login";
}

/** Read + validate `?returnTo=` from a search string. */
export function returnToFromSearch(search) {
  try {
    const rt = new URLSearchParams(search || "").get("returnTo");
    return sanitizeReturnTo(rt);
  } catch (e) {
    return null;
  }
}

/** Google OAuth leaves the SPA, so the intent is parked in sessionStorage
 *  and consumed by the `?token=` return leg in App.js. */
const PENDING_RETURN_TO_KEY = "pending_return_to";
export function stashPendingReturnTo(rt) {
  const clean = sanitizeReturnTo(rt);
  if (clean) sessionStorage.setItem(PENDING_RETURN_TO_KEY, clean);
  else sessionStorage.removeItem(PENDING_RETURN_TO_KEY);
}
export function takePendingReturnTo() {
  const rt = sessionStorage.getItem(PENDING_RETURN_TO_KEY);
  sessionStorage.removeItem(PENDING_RETURN_TO_KEY);
  return sanitizeReturnTo(rt);
}

// ---------------------------------------------------------------------------
// token claims + sliding refresh
// ---------------------------------------------------------------------------

/** Decode the JWT payload WITHOUT verifying (the browser can't and needn't —
 *  the backend verifies). Returns null on anything malformed. */
export function decodeTokenClaims(token) {
  if (typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const claims = JSON.parse(json);
    return claims && typeof claims === "object" ? claims : null;
  } catch (e) {
    return null;
  }
}

/** True once the token is past half its lifetime (needs `iat` + `exp`).
 *  Tokens without `iat` (minted before the rollout) refresh when less than
 *  12h remain, so they too roll onto the new lifetime on first use. */
export function shouldRefreshToken(token, nowSeconds = Date.now() / 1000) {
  const claims = decodeTokenClaims(token);
  if (!claims || typeof claims.exp !== "number") return false;
  if (claims.exp <= nowSeconds) return false; // already expired: refresh can't help
  if (typeof claims.iat === "number" && claims.iat < claims.exp) {
    const half = claims.iat + (claims.exp - claims.iat) / 2;
    return nowSeconds >= half;
  }
  return claims.exp - nowSeconds < 12 * 3600;
}

let refreshInFlight = null;

/** Exchange the stored token for a fresh one if it is past half-life.
 *  Resolves to true when a new token was stored. Never throws; a failed
 *  refresh simply leaves the current (still valid) token in place — the
 *  interceptor's 401 handling covers the case where it has actually died. */
export async function refreshSessionIfNeeded({ force = false } = {}) {
  const token = localStorage.getItem("token");
  if (!token) return false;
  if (!force && !shouldRefreshToken(token)) return false;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      // Raw fetch: the interceptor would treat a 401 here as "session
      // expired" and hard-redirect; a failed refresh must stay silent.
      const doFetch = window._originalFetch || window.fetch;
      const res = await doFetch(`${API()}auth/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res || !res.ok) return false;
      const data = await res.json().catch(() => null);
      if (!data || typeof data.token !== "string") return false;
      // Only replace the token if nothing else (another tab, org switch)
      // swapped it while we were in flight.
      if (localStorage.getItem("token") === token) {
        localStorage.setItem("token", data.token);
      }
      return true;
    } catch (e) {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// session teardown -> login (used by every 401 path)
// ---------------------------------------------------------------------------

/** Wipe the session keys and hard-redirect to /login, preserving where the
 *  user was (unless already on an auth page). `message` (already localized)
 *  is shown by the login page via sessionStorage. */
export function redirectToLogin({ message, clearRole } = {}) {
  localStorage.removeItem("token");
  localStorage.removeItem("user_id");
  localStorage.removeItem("first_name");
  if (typeof clearRole === "function") {
    try { clearRole(); } catch (e) { /* noop */ }
  }
  if (message) sessionStorage.setItem("auth_message", message);
  window.location.href = loginPathFor(window.location);
}

// ---------------------------------------------------------------------------
// Wix entry (Robert, 19.10.2026)
// ---------------------------------------------------------------------------
// Inside the Wix dashboard the portal runs in an iframe, whose storage the browser keeps apart from
// portal.ultimo-bots.com opened directly (where the install onboarding runs). It can still hold the
// session of an earlier visit: an expired token, another account, that account's selected agent.

/** The Wix entry pages sign in on their own: the ?instance= dashboard entry through /auth/wix/resolve,
 *  /wix-start and the /wix/<app>/ pages through their own flow. A 401 there must not open the portal login. */
export function isWixEntry(location = window.location) {
  const path = location.pathname || "";
  return path.startsWith("/wix-start") || path.startsWith("/wix/") ||
    new URLSearchParams(location.search || "").has("instance");
}

const SESSION_KEYS = ["token", "user_id", "first_name", "selectedBotId", "selectedBotIds"];

/** The Wix dashboard entry (?instance=, not a /wix/<app>/ page) drops whatever session this storage holds
 *  before anything renders: /auth/wix/resolve issues the session of the site's owner and selects its agent.
 *  Otherwise the first requests run with the old token, and its 401 sends the owner to the portal login. */
export function clearSessionForWixEntry(location = window.location, clearRole) {
  const path = location.pathname || "";
  if (path.startsWith("/wix/") || !new URLSearchParams(location.search || "").has("instance")) return false;
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  if (typeof clearRole === "function") {
    try { clearRole(); } catch (e) { /* noop */ }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Telegram login button (Seamless Telegram Login)
// ---------------------------------------------------------------------------

// The fields Telegram appends to a login_url callback. Anything else in the
// query string is ours (bot, session, ...) and must NOT be sent for
// verification (Telegram signs only its own fields).
export const TELEGRAM_LOGIN_FIELDS = ["id", "first_name", "last_name", "username", "photo_url", "auth_date", "hash"];

/** Split a location search string into { telegram, search } where `telegram`
 *  is the signed payload (or null when the URL carries none) and `search` is
 *  the remaining query string (leading "?" or ""). */
export function extractTelegramLogin(search) {
  let params;
  try {
    params = new URLSearchParams(search || "");
  } catch (e) {
    return { telegram: null, search: search || "" };
  }
  if (!params.has("hash") || !params.has("id") || !params.has("auth_date")) {
    return { telegram: null, search: search || "" };
  }
  const telegram = {};
  for (const key of TELEGRAM_LOGIN_FIELDS) {
    const value = params.get(key);
    if (value !== null && value !== "") telegram[key] = value;
    params.delete(key);
  }
  const rest = params.toString();
  return { telegram, search: rest ? `?${rest}` : "" };
}

let telegramExchangeInFlight = null;

/** POST the Telegram-signed payload; resolves to the login response object
 *  ({ token, user_id, ... }) or null on any refusal. Memoised while in
 *  flight so a double-invoked effect can't burn the single-use nonce twice. */
export async function exchangeTelegramLogin({ telegram, botId }) {
  if (!telegram) return null;
  if (telegramExchangeInFlight) return telegramExchangeInFlight;
  telegramExchangeInFlight = (async () => {
    try {
      const doFetch = window._originalFetch || window.fetch;
      const res = await doFetch(`${API()}auth/telegram/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telegram, bot_id: botId || null }),
      });
      if (!res || !res.ok) return null;
      const data = await res.json().catch(() => null);
      return data && typeof data.token === "string" ? data : null;
    } catch (e) {
      return null;
    } finally {
      telegramExchangeInFlight = null;
    }
  })();
  return telegramExchangeInFlight;
}
